/**
 * Video Reels Section JavaScript
 * Handles Swiper initialization and video playback controls
 * Uses Intersection Observer for performance
 */
(function() {
  'use strict';

  const whenVisible = window.theme?.whenVisible || ((element, callback) => callback(element));

  function initVideoReelsSection(container = document) {
    const sections = (container.classList && container.classList.contains('video-reels')) ? [container] : container.querySelectorAll('.video-reels');

    sections.forEach(function(section) {
      const swiperElement = section.querySelector('.swiper');
      if (!swiperElement || section.swiper) return;

      window.loadThemeSwiper().then(() => {
        initSwiper(section, swiperElement);
      }).catch((error) => {
        console.warn('Swiper failed to load for VideoReels:', error);
      });
    });
  }

  function initSwiper(section, swiperElement) {
    const navPrev = section.querySelector('.video-reels__nav-btn--prev');
    const navNext = section.querySelector('.video-reels__nav-btn--next');

    const slidesPerViewMobile = parseInt(section.dataset.slidesMobile) || 2;
    const slidesPerViewTablet = parseInt(section.dataset.slidesTablet) || 3;
    const slidesPerViewDesktop = parseInt(section.dataset.slidesDesktop) || 5;
    const spaceBetweenMobile = parseInt(section.dataset.spaceBetweenMobile) || 10;
    const spaceBetween = parseInt(section.dataset.spaceBetween) || 12;

    const options = {
      slidesPerView: slidesPerViewDesktop,
      spaceBetween: spaceBetween,
      loop: section.dataset.loop === 'true',
      autoplay: section.dataset.autoplay === 'true' ? {
        delay: parseInt(section.dataset.autoplayDelay) || 5000,
        disableOnInteraction: true
      } : false,
      breakpoints: {
        320: {
          slidesPerView: slidesPerViewMobile,
          spaceBetween: spaceBetweenMobile
        },
        480: {
          slidesPerView: slidesPerViewMobile,
          spaceBetween: spaceBetweenMobile + 2
        },
        640: {
          slidesPerView: slidesPerViewTablet,
          spaceBetween: spaceBetween + 3
        },
        900: {
          slidesPerView: slidesPerViewTablet + 1,
          spaceBetween: spaceBetween + 6
        },
        1100: {
          slidesPerView: slidesPerViewDesktop,
          spaceBetween: spaceBetween + 8
        }
      }
    };

    const swiper = new Swiper(swiperElement, {
      slidesPerView: options.slidesPerView,
      spaceBetween: options.spaceBetween,
      loop: options.loop,
      speed: 300,
      navigation: navPrev && navNext ? {
        nextEl: navNext,
        prevEl: navPrev
      } : false,
      breakpoints: options.breakpoints,
      autoplay: options.autoplay,
      on: {
        init: function() {
          setupPerformanceObserver(this.el);
        },
        slideChangeTransitionEnd: function() {
          checkVisibility(this.el);
        }
      }
    });

    section.swiper = swiper;
  }

  function setupPerformanceObserver(container) {
    const videos = container.querySelectorAll('[data-video-reels-video]');
    const soundButtons = container.querySelectorAll('[data-video-reels-sound-btn]');
    
    if (container.dataset.videoReelsInitialized === 'true') return;
    container.dataset.videoReelsInitialized = 'true';

    const observerOptions = {
      root: null, 
      rootMargin: '0px',
      threshold: 0.6
    };

    const videoObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          if (video.preload === 'none') {
            video.preload = 'auto';
          }

          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch(() => {
            });
          }
        } else {
          video.pause();
        }
      });
    }, observerOptions);

    videos.forEach(video => {
      videoObserver.observe(video);
      
      const card = video.closest('[data-video-reels-card]');
      if (card && !card.dataset.hasClickListener) {
        card.dataset.hasClickListener = "true";
        card.addEventListener('click', (e) => {
          if (e.target.closest('[data-video-reels-sound-btn]')) return;
          video.paused ? video.play().catch(()=>{}) : video.pause();
        });
      }
    });

    soundButtons.forEach((btn) => {
      if (btn.dataset.soundListenerAdded) return;
      btn.dataset.soundListenerAdded = 'true';
      
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = btn.closest('[data-video-reels-card]');
        const video = card.querySelector('[data-video-reels-video]');
        if (!video) return;

        if (video.muted) {
          muteAllExcept(video, container);
          video.muted = false;
          btn.classList.add('video-reels-card__sound-btn--unmuted');
        } else {
          video.muted = true;
          btn.classList.remove('video-reels-card__sound-btn--unmuted');
        }
      });
    });
  }

  function muteAllExcept(currentVideo, container) {
    const allVideos = container.querySelectorAll('[data-video-reels-video]');
    allVideos.forEach((video) => {
      if (video !== currentVideo) {
        video.muted = true;
        const card = video.closest('[data-video-reels-card]');
        if (card) {
          card.querySelector('[data-video-reels-sound-btn]')?.classList.remove('video-reels-card__sound-btn--unmuted');
        }
      }
    });
  }

  function checkVisibility(container) {
    const videos = container.querySelectorAll('[data-video-reels-video]');
    videos.forEach(video => {
       const rect = video.getBoundingClientRect();
       if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) {
          video.pause();
       }
    });
  }

  if (!customElements.get('video-reels-section')) {
    class VideoReelsSection extends HTMLElement {
      constructor() {
        super();
        this._onVisibilityChange = this._onVisibilityChange.bind(this);
      }

      connectedCallback() {
        this.visibilityObserver = whenVisible(this, () => initVideoReelsSection(this));
        document.addEventListener('visibilitychange', this._onVisibilityChange);
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect?.();
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
        if (this.swiper) {
          this.swiper.destroy(true, true);
        }
      }

      _onVisibilityChange() {
        if (document.hidden) {
          const videos = this.querySelectorAll('video');
          videos.forEach(v => v.pause());
        } else {
          const videos = this.querySelectorAll('[data-video-reels-video]');
          videos.forEach(video => {
            const rect = video.getBoundingClientRect();
            const isVisible = !(rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth);
            if (isVisible) {
              video.play().catch(() => {});
            }
          });
        }
      }
    }
    customElements.define('video-reels-section', VideoReelsSection);
  }
})();
